/** Klien API booking & pembayaran sisi pasien. */

import { api } from "./client";

export type BookingScope = "upcoming" | "history";

/**
 * Payload POST /bookings. Terapis TIDAK dikirim klien: server menetapkan
 * terapis yang berdinas secara adil, lalu mengembalikannya di respons.
 */
export interface CreateBookingPayload {
  tenantSlug: string;
  serviceId: string;
  date: string; // "YYYY-MM-DD"
  startTime: string; // "HH:mm"
  notes?: string;
  payOnArrival?: boolean; // true = bayar di tempat (langsung CONFIRMED, tanpa payment)
}

/** Terapis yang ditetapkan sistem untuk booking yang baru dibuat. */
export interface AssignedTherapist {
  id: string;
  name: string;
  gender: "L" | "P" | null;
}

export interface CreateBookingResult {
  booking: { id: string; bookingCode: string; status: string; payOnArrival: boolean };
  payment: {
    id: string;
    paymentCode: string;
    status: string;
    amount: number;
    expiresAt: string;
  } | null;
  therapist: AssignedTherapist;
}

/** Ringkasan pembayaran yang menyertai item booking (bisa null). */
export interface BookingPaymentRef {
  id: string;
  paymentCode?: string;
  provider?: string;
  status: string;
  amount: number;
  qrContent?: string | null;
  expiresAt?: string | null;
  paidAt?: string | null;
}

/** Penilaian bintang yang sudah diberikan pasien untuk satu booking. */
export interface BookingReview {
  id: string;
  rating: number; // 1..5
  comment: string | null;
  createdAt: string;
}

/** Satu baris daftar booking (GET /bookings). */
export interface BookingListItem {
  id: string;
  bookingCode: string;
  bookingDate: string; // "YYYY-MM-DD" lokal tenant
  startTime: string; // "HH:mm"
  endTime: string;
  priceSnapshot: number;
  status: string;
  payOnArrival: boolean;
  notes?: string | null;
  tenant: { id: string; name: string; slug: string };
  service: { id: string; name: string; durationMinutes: number };
  therapist: { id: string; name: string };
  payment: BookingPaymentRef | null;
  review: BookingReview | null;
}

/** Detail booking lengkap (GET /bookings/:id). */
export interface BookingDetail {
  id: string;
  bookingCode: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  priceSnapshot: number;
  status: string;
  payOnArrival: boolean;
  notes?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    address: string | null;
    phone: string | null;
    timezone: string;
  };
  service: {
    id: string;
    name: string;
    description: string | null;
    durationMinutes: number;
    price: number;
  };
  therapist: {
    id: string;
    name: string;
    photoUrl: string | null;
    specialization: string | null;
    bio: string | null;
  };
  payment: BookingDetailPayment | null;
  review: BookingReview | null;
}

/** Pembayaran pada detail booking. expiresAt/paidAt berupa ISO string dari server. */
export interface BookingDetailPayment {
  id: string;
  paymentCode: string;
  provider: string;
  amount: number;
  status: string;
  qrContent: string | null;
  expiresAt: string;
  paidAt: string | null;
}

export function createBooking(payload: CreateBookingPayload): Promise<CreateBookingResult> {
  return api.post<CreateBookingResult>("/bookings", payload);
}

export function fetchBookings(scope: BookingScope): Promise<BookingListItem[]> {
  return api
    .get<{ bookings: BookingListItem[] }>(`/bookings?scope=${scope}`)
    .then((res) => res.bookings);
}

export function fetchBooking(id: string): Promise<BookingDetail> {
  return api.get<{ booking: BookingDetail }>(`/bookings/${id}`).then((res) => res.booking);
}

export function cancelBooking(
  id: string,
  reason?: string,
): Promise<{ booking: { id: string; status: string } }> {
  return api.post<{ booking: { id: string; status: string } }>(`/bookings/${id}/cancel`, {
    reason: reason?.trim() || undefined,
  });
}

/** Beri penilaian bintang 1–5 untuk sesi yang sudah selesai. */
export function submitReview(
  id: string,
  payload: { rating: number; comment?: string },
): Promise<{ review: BookingReview }> {
  return api.post<{ review: BookingReview }>(`/bookings/${id}/review`, {
    rating: payload.rating,
    comment: payload.comment?.trim() || undefined,
  });
}

export function fetchPayment(id: string): Promise<{ payment: BookingDetailPayment }> {
  return api.get<{ payment: BookingDetailPayment }>(`/payments/${id}`);
}

export function ensureQris(
  bookingId: string,
): Promise<{ payment: BookingDetailPayment; regenerated: boolean }> {
  return api.post<{ payment: BookingDetailPayment; regenerated: boolean }>(
    `/bookings/${bookingId}/qris`,
  );
}

export function devPay(
  paymentCode: string,
): Promise<{ ok: boolean; handled: boolean; status: string }> {
  return api.post<{ ok: boolean; handled: boolean; status: string }>("/payments/dev/pay", {
    paymentCode,
  });
}
